import React, { useEffect, useRef, useState } from 'react'
import Col from 'antd/lib/col'
import Row from 'antd/lib/row'
import Form from 'antd/lib/form'
import Button from 'antd/lib/button'
import Switch from 'antd/lib/switch'

import PlusCircleOutlined from '@ant-design/icons/PlusCircleOutlined'
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined'
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined'
import CutoffTimeFields from './CutoffFields'
import moment from 'moment'
import { uuid } from 'uuidv4'
import format from 'date-fns/format'
import includes from 'lodash/includes'
import omit from 'lodash/omit'
import { FaChevronUp, FaChevronDown } from 'react-icons/fa'
import {
  StyledPastSO,
  StyledButton,
  StyledHeader,
  PastSOContainer,
  StyledDatePicker
} from 'components/Common'
import PageHeader from 'antd/lib/page-header'
import Tooltip from 'antd/lib/tooltip'

type Cutoff = {
  id: string
  enabled: boolean
  time: string | null
  day_diff: number | null
  cutoff_date: string | null
}

const SpecialCutoffTimesForm = ({
  cutoffs,
  setCutoffs,
  disabled = false
}: {
  cutoffs: { [key: string]: Cutoff }
  setCutoffs: (cutoffs: { [key: string]: Cutoff }) => void
  disabled?: boolean
}) => {
  const contentRef = useRef(null)
  const threeMonthsAgo = moment().subtract(3, 'months')
  const [height, setHeight] = useState<number>(0)
  const [showPastThreeMonths, setShowPastThreeMonths] = useState<boolean>(false)

  useEffect(() => {
    if (contentRef.current) {
      // @ts-ignore
      setHeight(contentRef.current.scrollHeight)
    }
  }, [showPastThreeMonths])

  const usedDates = Object.keys(cutoffs).map(
    (key) => cutoffs[key].cutoff_date || ''
  )
  const removeCutoff = (id: string) => setCutoffs(omit(cutoffs, id))

  const updateCutoff = (
    id: string,
    key: string,
    value: string | boolean | number
  ) => setCutoffs({ ...cutoffs, [id]: { ...cutoffs[id], [key]: value } })

  const presentFutureCutoffs = Object.values(cutoffs).filter(
    ({ cutoff_date }) =>
      cutoff_date == '' || moment(cutoff_date) > threeMonthsAgo
  )
  const pastCutoffs = Object.values(cutoffs).filter(
    ({ cutoff_date }) => moment(cutoff_date) < threeMonthsAgo
  )

  return (
    <>
      <Row className='_mb-0'>
        <Col span={12}>
          <Row className='_mb-0'>
            <PageHeader
              title='Special Cut-off times'
              className='settings-title'
            />
            <Tooltip
              placement='right'
              title='Special cut-off times let you change your usual cut-offs. You can either extend or restrict order placements beyond your normal cut-off times.'
            >
              <QuestionCircleOutlined style={{ paddingBottom: 20 }} />
            </Tooltip>
          </Row>
        </Col>
        <Col span={12}>
          <Row className='_mb-0' justify='end'>
            <Col>
              <AddButton
                disabled={disabled}
                onClick={() => {
                  const id = uuid()
                  setCutoffs({
                    ...cutoffs,
                    [id]: {
                      id,
                      enabled: true,
                      time: '00:00:00',
                      day_diff: 1,
                      cutoff_date: ''
                    }
                  })
                }}
              />
            </Col>
            <Col span={2} />
          </Row>
        </Col>
      </Row>
      {presentFutureCutoffs.map((cutoff) => {
        return (
          <SpecialCutoffTime
            key={`special-cutoff-${cutoff.id}`}
            handleDelete={() => removeCutoff(cutoff.id)}
            usedDates={usedDates}
            updateCutoff={(key, value) => updateCutoff(cutoff.id, key, value)}
            cutoff={cutoff}
            disabled={disabled}
          />
        )
      })}
      <Row className='_mb-4'>
        <Col span={12}>
          <StyledHeader>Past Special cut-off times</StyledHeader>
        </Col>
        <Col span={12}>
          <StyledButton
            onClick={() => setShowPastThreeMonths(!showPastThreeMonths)}
          >
            <Row justify='end' align='middle' className='_mb-0' gutter={8}>
              {showPastThreeMonths ? (
                <>
                  <Col>Hide Past Cut-off times</Col>
                  <Col>
                    <Row className='_mb-0' justify='end'>
                      <FaChevronUp />
                    </Row>
                  </Col>
                  <Col span={2} />
                </>
              ) : (
                <>
                  <Col>See Past Cut-off times</Col>
                  <Col>
                    <Row className='_mb-0' justify='end'>
                      <FaChevronDown />
                    </Row>
                  </Col>
                  <Col span={2} />
                </>
              )}
            </Row>
          </StyledButton>
        </Col>
      </Row>
      <Row>
        <StyledPastSO>
          Past special cut-off times will automatically be deleted after 3
          months. You can always refer to them before the period ends.
        </StyledPastSO>
      </Row>
      <Row>
        <Col span={24}>
          <PastSOContainer isOpen={showPastThreeMonths} height={height}>
            <div ref={contentRef}>
              {pastCutoffs.map((cutoff) => {
                return (
                  <SpecialCutoffTime
                    key={`special-cutoff-${cutoff.id}`}
                    handleDelete={() => removeCutoff(cutoff.id)}
                    usedDates={usedDates}
                    updateCutoff={(key, value) =>
                      updateCutoff(cutoff.id, key, value)
                    }
                    cutoff={cutoff}
                    disabled={disabled}
                  />
                )
              })}
            </div>
          </PastSOContainer>
        </Col>
      </Row>
    </>
  )
}

const AddButton = ({
  onClick,
  disabled = false
}: {
  onClick: () => void
  disabled?: boolean
}) => {
  return (
    <>
      <Button
        icon={<PlusCircleOutlined />}
        onClick={onClick}
        disabled={disabled}
        className='_mb-8'
      >
        Add Special cut-off times
      </Button>
    </>
  )
}

const SpecialCutoffTime = ({
  cutoff,
  handleDelete,
  usedDates,
  updateCutoff,
  disabled = false
}: {
  cutoff: Cutoff
  handleDelete: () => void
  usedDates: string[]
  updateCutoff: (key: string, value: boolean | number | string) => void
  disabled?: boolean
}) => {
  return (
    <Form.Item labelAlign='left'>
      <Row align='middle' justify='end' className='_mt-8 _mb-0'>
        <Col span={12}>
          <Row gutter={8} className='_mb-0' align='middle'>
            <Col>
              <Switch
                onChange={(checked) => updateCutoff('enabled', checked)}
                defaultChecked={cutoff.enabled}
                disabled={disabled}
              />
            </Col>
            <Col>
              <StyledDatePicker
                autoFocus={!cutoff.cutoff_date}
                defaultValue={
                  cutoff.cutoff_date ? moment(cutoff.cutoff_date) : undefined
                }
                disabledDate={(date) =>
                  date.isBefore(moment()) ||
                  includes(usedDates, format(date.toDate(), 'yyyy-MM-dd'))
                }
                onChange={(date) =>
                  date &&
                  updateCutoff(
                    'cutoff_date',
                    format(date.toDate(), 'yyyy-MM-dd')
                  )
                }
                allowClear={false}
                disabled={disabled}
                format='DD/MM/YYYY'
              />
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Row gutter={8} className='_mb-0' align='middle' justify='end'>
            <Col>
              <CutoffTimeFields
                disabled={!cutoff.enabled}
                dayDiff={cutoff.day_diff}
                time={cutoff.time}
                updateCutoff={updateCutoff}
              />
            </Col>
            <Col span={2} className='_center-vertical'>
              <CloseCircleOutlined
                className='_text-font-size-18 _cl-primary'
                onClick={handleDelete}
                disabled={disabled}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Form.Item>
  )
}

export default SpecialCutoffTimesForm
